// BoardingServiceCheckinCard.jsx
import { useState, useEffect, useRef } from "react";
import { httpService } from "../../services/httpService";
import { toast } from "react-toastify";
import {
  FaPlus,
  FaTimesCircle,
  FaUndo,
  FaEllipsisV,
  FaInfoCircle,
  FaCheckCircle,
} from "react-icons/fa";
import CustomSelect from "./CustomSelect";
import {
  Box,
  Chip,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import PetsIcon from "@mui/icons-material/Pets";
import BookingAddonsSection from "./BookingAddonsSection";
import { useDrawerPrefill } from "../../hooks/useDrawerPrefill";

const formatLocalDate = (isoString) => {
  if (!isoString) return "";
  const d = new Date(isoString);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getBoardingDates = (checkin, checkout) => {
  if (!checkin || !checkout) return [];
  const start = new Date(checkin);
  const end = new Date(checkout);
  const dates = [];
  let current = new Date(start);
  while (current <= end) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return dates;
};

const toggleInArray = (setter, value) => {
  setter((prev) =>
    prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
  );
};

const formatDateDisplay = (dateObj) => {
  return dateObj.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

const DateChips = ({ dates, size = "small" }) => {
  if (!dates?.length) return null;
  return (
    <>
      {[...dates].sort().map((d) => (
        <Chip key={d} label={formatDateDisplay(new Date(d))} size={size} color="secondary" variant="outlined" />
      ))}
    </>
  );
};

const BoardingServiceCheckinCard = ({
  pet,
  service,
  index,
  onUpdate,
  clientId,
  onCancel,
  onModify,
  readOnly = false,
}) => {
  // ---------- Local state (UI values) ----------
  const [serviceId, setServiceId] = useState(service.service_id || service.serviceId || "");
  const [roomTypeId, setRoomTypeId] = useState(service.room_type_id || service.roomTypeId || "");
  const [roomId, setRoomId] = useState(service.room_id || service.roomId || "");
  const boardingDates = getBoardingDates(service.checkin, service.checkout);

  // ---------- Original snapshot (deep clone) ----------
  const originalSnapshot = useRef(null);
  useEffect(() => {
    if (!originalSnapshot.current) {
      originalSnapshot.current = JSON.parse(JSON.stringify(service));
    }
  }, []);

  // ---------- Other state ----------
  const [boardingServices, setBoardingServices] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [loadingRoomTypes, setLoadingRoomTypes] = useState(false);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [priceLoading, setPriceLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const breakdownRef = useRef(null);

  // ---------- Prefill from submitted consent form ----------
  const {
    prefill,
    sourceForm,
    clear: clearPrefill,
  } = useDrawerPrefill({
    open: !readOnly,
    orderId: service.order_id || service.orderId || "",
    refId: service.ref_id || service.service_ref_id || service.id || "",
  });
  const prefillAppliedRef = useRef(false);

  // ---------- Form state ----------
  const [feedDates, setFeedDates] = useState([]);
  const [walkDates, setWalkDates] = useState([]);
  const [medDates, setMedDates] = useState([]);
  const [feedPeriods, setFeedPeriods] = useState([]);
  const [feedWhat, setFeedWhat] = useState("");
  const [walkTimes, setWalkTimes] = useState([]);
  const [walkDur, setWalkDur] = useState("");
  const [walkNotes, setWalkNotes] = useState("");
  const [medPeriods, setMedPeriods] = useState([]);
  const [medName, setMedName] = useState("");
  const [medQty, setMedQty] = useState("");
  const [medRoute, setMedRoute] = useState("");
  const [medInstr, setMedInstr] = useState("");
  const [belongName, setBelongName] = useState("");
  const [belongQty, setBelongQty] = useState("1");
  const [belongDesc, setBelongDesc] = useState("");

  useEffect(() => {
    if (!prefill || prefillAppliedRef.current) return;
    if (!Object.keys(prefill).length) return;
    prefillAppliedRef.current = true;

    const bookingDateKeys = boardingDates.map((d) => formatLocalDate(d));

    // ── FEEDING ───────────────────────────────────────────────────────
    const firstFeeding = (prefill.feeding || [])[0];
    let whatToFeed = "";
    let normalizedPeriodsForFeed = [];

    if (firstFeeding) {
      whatToFeed = [
        firstFeeding.whatToFeed,
        firstFeeding.quantity,
        firstFeeding.mealType,
        firstFeeding.mealsPerDay ? `${firstFeeding.mealsPerDay}/day` : "",
      ]
        .filter(Boolean)
        .join(" · ");

      const periodsRaw = Array.isArray(firstFeeding.periods)
        ? firstFeeding.periods
        : String(firstFeeding.periods || "")
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);

      normalizedPeriodsForFeed = periodsRaw
        .map((p) => {
          const v = String(p).trim().toLowerCase();
          if (v === "afternoon" || v === "noon") return "Noon";
          if (v === "morning") return "Morning";
          if (v === "evening") return "Evening";
          return null;
        })
        .filter(Boolean);

      setFeedWhat(whatToFeed);
      setFeedPeriods(normalizedPeriodsForFeed);
      setFeedDates(bookingDateKeys);
    }

    // ── MEDICATION ────────────────────────────────────────────────────
    const firstMed = (prefill.medication || [])[0];
    let normalizedPeriodsForMed = [];

    if (firstMed) {
      setMedName(firstMed.name || "");
      setMedQty(firstMed.quantity || "");
      setMedRoute(firstMed.route || "oral");
      setMedInstr(firstMed.instructions || "");

      const periodsRaw = Array.isArray(firstMed.periods)
        ? firstMed.periods
        : String(firstMed.periods || "")
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);

      normalizedPeriodsForMed = periodsRaw
        .map((p) => {
          const v = String(p).trim().toLowerCase();
          if (v === "afternoon" || v === "noon") return "Noon";
          if (v === "morning") return "Morning";
          if (v === "evening") return "Evening";
          return null;
        })
        .filter(Boolean);

      setMedPeriods(normalizedPeriodsForMed);
      setMedDates(bookingDateKeys);
    }

    // ── WALKING ───────────────────────────────────────────────────────
    const firstWalk = (prefill.walking || [])[0];
    let normalizedTimesForWalk = [];

    if (firstWalk) {
      const timesRaw = Array.isArray(firstWalk.times)
        ? firstWalk.times
        : String(firstWalk.times || "")
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);

      normalizedTimesForWalk = timesRaw
        .map((p) => {
          const v = String(p).trim().toLowerCase();
          if (v === "afternoon" || v === "noon") return "Noon";
          if (v === "morning") return "Morning";
          if (v === "evening") return "Evening";
          return null;
        })
        .filter(Boolean);

      setWalkTimes(normalizedTimesForWalk);
      setWalkDur(firstWalk.duration || "");
      setWalkNotes(firstWalk.notes || "");
      setWalkDates(bookingDateKeys);
    }

    // ── BELONGINGS ────────────────────────────────────────────────────
    const firstBelonging = (prefill.belongings || [])[0];
    const nextBelongings =
      firstBelonging && !(service.belongings || []).length
        ? [
            {
              name: firstBelonging.name || "",
              quantity: firstBelonging.quantity || "1",
              description: firstBelonging.description || "",
            },
          ]
        : service.belongings || [];

    // ── COMMIT PREFILL INTO service.* SO CHECK-IN PICKS IT UP ─────────
    //
    // The inputs above are UI-only. `buildDailySchedule(service)` on the
    // parent reads `service.feeding` / `service.medication` /
    // `service.walking`. If we don't also write those arrays, the
    // check-in payload's `daily_schedule` will be empty.
    //
    // Existing `service.*` rows always win — we don't clobber anything
    // the vendor has already saved.

    const nextFeeding =
      (service.feeding || []).length > 0
        ? service.feeding
        : firstFeeding
          ? [
              {
                periods: normalizedPeriodsForFeed,
                whatToFeed: whatToFeed,
                dates: bookingDateKeys,
              },
            ]
          : [];

    const nextMedication =
      (service.medication || []).length > 0
        ? service.medication
        : firstMed
          ? [
              {
                periods: normalizedPeriodsForMed,
                name: firstMed.name || "",
                route: firstMed.route || "oral",
                quantity: firstMed.quantity || "",
                instructions: firstMed.instructions || "",
                dates: bookingDateKeys,
              },
            ]
          : [];

    const nextWalking =
      (service.walking || []).length > 0
        ? service.walking
        : firstWalk
          ? [
              {
                times: normalizedTimesForWalk,
                duration: firstWalk.duration || "",
                notes: firstWalk.notes || "",
                dates: bookingDateKeys,
              },
            ]
          : [];

    onUpdate({
      ...service,
      feeding: nextFeeding,
      medication: nextMedication,
      walking: nextWalking,
      belongings: nextBelongings,
    });

    // Auto-select the tab with the most data
    if (prefill.medication?.length) setActiveTab(2);
    else if (prefill.feeding?.length) setActiveTab(0);
    else if (prefill.walking?.length) setActiveTab(1);
    else if (prefill.belongings?.length) setActiveTab(3);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

    // Mirror the local input state into `service.feeding` so Check-In
  // sees whatever is in the inputs — typed or pre-filled — without
  // requiring the vendor to explicitly click "+ Add Feeding".
  useEffect(() => {
    // Only mirror when there are no saved rows yet. Once the vendor
    // clicks "+ Add Feeding", we stop mirroring so we don't fight
    // the array.
    if ((service.feeding || []).length > 0) return;
    if (!feedWhat && feedPeriods.length === 0 && feedDates.length === 0) return;

    onUpdate({
      ...service,
      feeding: [
        {
          periods: feedPeriods,
          whatToFeed: feedWhat,
          dates: feedDates,
        },
      ],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedWhat, feedPeriods, feedDates]);

  useEffect(() => {
    if ((service.medication || []).length > 0) return;
    if (!medName && medPeriods.length === 0 && medDates.length === 0) return;

    onUpdate({
      ...service,
      medication: [
        {
          periods: medPeriods,
          name: medName,
          route: medRoute || "oral",
          quantity: medQty,
          instructions: medInstr,
          dates: medDates,
        },
      ],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medName, medQty, medRoute, medInstr, medPeriods, medDates]);

  useEffect(() => {
    if ((service.walking || []).length > 0) return;
    if (!walkDur && walkTimes.length === 0 && walkDates.length === 0) return;

    onUpdate({
      ...service,
      walking: [
        {
          times: walkTimes,
          duration: walkDur,
          notes: walkNotes,
          dates: walkDates,
        },
      ],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walkTimes, walkDur, walkNotes, walkDates]);
  
  const isInitialMount = useRef(true);

  const selectedService = boardingServices.find((s) => s._id === serviceId);
  const selectedRoomType = roomTypes.find((rt) => rt._id === roomTypeId);
  const selectedRoom = availableRooms.find((r) => r._id === roomId);

  const nights =
    service.checkin && service.checkout
      ? Math.max(1, Math.ceil((new Date(service.checkout) - new Date(service.checkin)) / 86400000))
      : 0;

  // ✅ Service + room type must both be selected for a valid price
  const isServiceFullyConfigured = Boolean(serviceId && roomTypeId);

  const lastPriceParamsRef = useRef("");

  // ---------- Reset ----------
  const handleReset = () => {
    if (!originalSnapshot.current) return;
    const snap = JSON.parse(JSON.stringify(originalSnapshot.current));

    // Reset local UI state to snapshot
    setServiceId(snap.service_id || snap.serviceId || "");
    setRoomTypeId(snap.room_type_id || snap.roomTypeId || "");
    setRoomId(snap.room_id || snap.roomId || "");

    // ✅ Force the price effect to re-run for this combination.
    // The `lastPriceParamsRef` dedupe will still skip truly identical params,
    // but we explicitly clear it here so undo always triggers a fresh calc.
    lastPriceParamsRef.current = "";

    onUpdate({
      ...snap,
      products: service.products || [],
      addons: service.addons || [],
      otherCharges: service.otherCharges || [],
      serviceName: snap.serviceName || "",
      roomType: snap.roomType || "",
      roomNumber: snap.roomNumber || "",
    });
  };

  // ---------- Fetch boarding services ----------
  useEffect(() => {
    if (readOnly) return;
    const fetchBoardingServices = async () => {
      try {
        const res = await httpService.get(
          `/vendorroomservice/getRoomServices?status=active&petType=${pet.petType}`
        );
        setBoardingServices(res?.data || []);
      } catch (err) {
        console.error("Error fetching boarding services:", err);
      }
    };
    fetchBoardingServices();
  }, [readOnly]);

  // ---------- Room types ----------
  useEffect(() => {
    if (readOnly) { setRoomTypes([]); return; }
    if (serviceId && service.checkin && service.checkout && pet.petType && boardingServices.length > 0) {
      setLoadingRoomTypes(true);
      httpService
        .post("/room/getAvailableVendorRooms", {
          checkIn: service.checkin,
          checkOut: service.checkout,
          type: "boarding",
          petType: pet.petType,
          serviceId: serviceId,
        })
        .then((res) => {
          const allowedRoomTypeIds = new Set(
            selectedService?.room_type_surcharges?.options?.map((o) => o.room_type_id) || []
          );
          const filteredRooms = (res.data || []).filter((room) => allowedRoomTypeIds.has(room._id));
          setRoomTypes(filteredRooms);
        })
        .catch(() => toast.error("Failed to load room types"))
        .finally(() => setLoadingRoomTypes(false));
    } else {
      setRoomTypes([]);
    }
  }, [readOnly, boardingServices, serviceId, service.checkin, service.checkout, pet.petType]);

  // ---------- Available rooms ----------
  useEffect(() => {
    if (readOnly) { setAvailableRooms([]); return; }
    if (roomTypeId && service.checkin && service.checkout && pet.petType) {
      setLoadingRooms(true);
      const payload = {
        checkIn: service.checkin,
        checkOut: service.checkout,
        type: "boarding",
        excludingBoardingId: service?.ref_id || null,
        petType: pet?.petType || "",
      };
      httpService
        .post(`/room/getAvailableVendorRooms`, payload)
        .then((res) => {
          const roomData = res.data?.find((item) => item._id === roomTypeId);
          setAvailableRooms(roomData?.available_room_numbers || []);
        })
        .catch(() => toast.error("Failed to load rooms"))
        .finally(() => setLoadingRooms(false));
    } else {
      setAvailableRooms([]);
    }
  }, [readOnly, roomTypeId, service.checkin, service.checkout, pet.petType]);

  const hasUserInteracted = useRef(false);

  // ---------- Price calculation ----------
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (!hasUserInteracted.current) return;

    // ⚠️ NOTE: We intentionally do NOT skip when hasChanges === false.
    // The price must always reflect the current (serviceId, roomTypeId,
    // checkin, checkout) tuple — even if the tuple equals the original
    // snapshot (e.g. after clicking Undo). Deduplication is handled below.

    // ✅ If service / room type not both set, reset price to 0 and bail out
    if (!serviceId || !roomTypeId) {
      const clearedService = {
        ...service,
        serviceId,
        service_id: serviceId,
        serviceName: selectedService?.name || service.serviceName,
        roomTypeId,
        room_type_id: roomTypeId,
        roomType: selectedRoomType?.room_name || service.roomType,
        roomId,
        room_id: roomId,
        roomNumber: selectedRoom?.room_number || service.roomNumber,
        price: 0,
        originalPrice: service.originalPrice ?? service.price,
        surchargeDetails: [],
        surchargesTotal: 0,
        discount_amount: 0,
        sale_price: 0,
        unit_price: 0,
        tax_percentage: 0,
        gst_amount: 0,
      };
      onUpdate(clearedService);
      return;
    }

    if (!clientId || !pet.id || !service.checkin || !service.checkout) {
      const updatedService = {
        ...service,
        serviceId,
        service_id: serviceId,
        serviceName: selectedService?.name || service.name,
        roomTypeId,
        room_type_id: roomTypeId,
        roomType: selectedRoomType?.room_name || service.roomType,
        roomId,
        room_id: roomId,
        roomNumber: selectedRoom?.room_number || service.roomNumber,
        price: 0,
        originalPrice: service.originalPrice ?? service.price,
        surchargeDetails: [],
        surchargesTotal: 0,
        discount_amount: 0,
        sale_price: 0,
      };
      onUpdate(updatedService);
      return;
    }

    const paramsKey = `${clientId}_${pet.id}_${serviceId}_${roomTypeId}_${service.checkin}_${service.checkout}`;
    if (lastPriceParamsRef.current === paramsKey) return;

    lastPriceParamsRef.current = paramsKey;
    setPriceLoading(true);

    const payload = {
      client_id: clientId,
      pet_id: pet.id,
      room_service_id: serviceId,
      room_type_id: roomTypeId,
      checkin_date: service.checkin,
      checkout_date: service.checkout,
    };

    httpService
      .post("/vendorbooking/getPriceCalculation?service_type=boarding", payload)
      .then((response) => {
        let price = 0;
        let salePrice = 0;
        let taxAmount = 0;
        let taxPercentage = 0;
        let discountAmount = 0;
        let surchargeDetails = [];
        let surchargesTotal = 0;

        if (typeof response.data === "number") {
          price = response.data;
          salePrice = response.data;
        } else if (response.data) {
          price = response.data.price || 0;
          salePrice = response.data.sale_price || price;
          taxAmount = response.data.discounted_tax || response.data.taxAmount || 0;
          taxPercentage = response.data.taxPercentage || 0;
          discountAmount = response.data.discount_amount || 0;
          surchargeDetails = response.data.surchargeDetails || [];
          surchargesTotal = surchargeDetails.reduce((sum, s) => sum + (s.price || 0), 0);
        }

        const totalPrice = salePrice + surchargesTotal + taxAmount;
        const unitPrice = nights > 0 ? totalPrice / nights : totalPrice;

        const updatedService = {
          ...service,
          serviceId,
          service_id: serviceId,
          serviceName: selectedService?.name || service.serviceName,
          roomTypeId,
          room_type_id: roomTypeId,
          roomType: selectedRoomType?.room_name || service.roomType,
          roomId,
          room_id: roomId,
          roomNumber: selectedRoom?.room_number || service.roomNumber,
          price: totalPrice,
          unit_price: unitPrice,
          tax_percentage: taxPercentage,
          gst_amount: taxAmount,
          discount_amount: discountAmount,
          sale_price: salePrice,
          surchargeDetails,
          surchargesTotal,
          originalPrice: service.originalPrice ?? service.price,
        };

        onUpdate(updatedService);
      })
      .catch(() => {
        toast.error("Failed to calculate price");
        const updatedService = {
          ...service,
          serviceId,
          service_id: serviceId,
          serviceName: selectedService?.name || service.serviceName,
          roomTypeId,
          room_type_id: roomTypeId,
          roomType: selectedRoomType?.room_name || service.roomType,
          roomId,
          room_id: roomId,
          roomNumber: selectedRoom?.room_number || service.roomNumber,
          price: service.originalPrice ?? service.price,
          originalPrice: service.originalPrice ?? service.price,
          surchargeDetails: [],
          surchargesTotal: 0,
          discount_amount: 0,
          sale_price: 0,
        };
        onUpdate(updatedService);
      })
      .finally(() => setPriceLoading(false));
  }, [
    clientId,
    pet.id,
    serviceId,
    roomTypeId,
    service.checkin,
    service.checkout,
    nights,
  ]);

  // ---------- Boarding data handlers ----------
  const addFeeding = (item, dates) => {
    onUpdate({ ...service, feeding: [...(service.feeding || []), { ...item, dates }] });
  };
  const removeFeeding = (idx) => {
    onUpdate({ ...service, feeding: (service.feeding || []).filter((_, i) => i !== idx) });
  };
  const addWalking = (item, dates) => {
    onUpdate({ ...service, walking: [...(service.walking || []), { ...item, dates }] });
  };
  const removeWalking = (idx) => {
    onUpdate({ ...service, walking: (service.walking || []).filter((_, i) => i !== idx) });
  };
  const addMedication = (item, dates) => {
    onUpdate({ ...service, medication: [...(service.medication || []), { ...item, dates }] });
  };
  const removeMedication = (idx) => {
    onUpdate({ ...service, medication: (service.medication || []).filter((_, i) => i !== idx) });
  };
  const addBelonging = (item) => {
    onUpdate({ ...service, belongings: [...(service.belongings || []), item] });
  };
  const removeBelonging = (idx) => {
    onUpdate({ ...service, belongings: (service.belongings || []).filter((_, i) => i !== idx) });
  };

  // ---------- Diff detection (for Reset button visibility + strikethrough) ----------
  const hasChanges =
    originalSnapshot.current &&
    ((originalSnapshot.current.service_id || originalSnapshot.current.serviceId) !== serviceId ||
      (originalSnapshot.current.room_type_id || originalSnapshot.current.roomTypeId) !== roomTypeId ||
      originalSnapshot.current.checkin !== service.checkin ||
      originalSnapshot.current.checkout !== service.checkout);

  // ---------- Click outside for breakdown ----------
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (breakdownRef.current && !breakdownRef.current.contains(event.target)) {
        setShowBreakdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ---------- Render ----------
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mb-4">
      <div className="px-4 py-3">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs font-bold uppercase px-2.5 py-1 rounded-full bg-blue-100 text-blue-800">
              Boarding
            </span>

            <div className="flex items-center gap-4 text-sm text-gray-700">
              <div className="flex items-center gap-1">
                <span className="font-semibold text-gray-500 text-xs">CheckIn:</span>
                <span className="font-medium">
                  {service.checkin
                    ? new Date(service.checkin).toLocaleDateString("en-IN", {
                        month: "numeric", day: "numeric", year: "numeric",
                      })
                    : "—"}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span className="font-semibold text-gray-500 text-xs">CheckOut:</span>
                {readOnly ? (
                  <span className="font-medium">
                    {service.checkout
                      ? new Date(service.checkout).toLocaleDateString("en-IN", {
                          month: "numeric", day: "numeric", year: "numeric",
                        })
                      : "—"}
                  </span>
                ) : (
                  <input
                    type="date"
                    value={formatLocalDate(service.checkout)}
                    min={service.checkin ? formatLocalDate(service.checkin) : undefined}
                    onChange={(e) => {
                      hasUserInteracted.current = true;
                      const newCheckout = e.target.value ? new Date(e.target.value) : null;
                      onUpdate({ ...service, checkout: newCheckout });
                    }}
                    className="text-sm font-medium text-gray-800 border border-gray-200 rounded-md px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-indigo-500 w-32"
                  />
                )}
              </div>
              <div className="flex items-center gap-1">
                <span className="font-bold text-gray-900">
                  {nights} {nights === 1 ? "Night" : "Nights"}
                </span>
              </div>
            </div>

            <span className="text-sm font-medium text-gray-700 bg-gray-100 px-2 py-0.5 rounded-md">
              {service.status === "confirmed" && service.is_queue == 0
                ? "Scheduled"
                : service.status === "" && service.is_queue == 1
                  ? "Queue"
                  : service.status}
            </span>

            {service.status != "cancelled" && (
              <div className="relative">
                <button
                  onClick={() => setMenuOpen((prev) => !prev)}
                  className="p-1 rounded-full hover:bg-gray-200 transition"
                  title="More actions"
                >
                  <FaEllipsisV size={14} className="text-gray-600" />
                </button>
                {menuOpen && (
                  <div className="absolute left-0 mt-1 w-36 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    <button
                      className="block w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-t-lg"
                      onClick={() => { setMenuOpen(false); onCancel && onCancel(service); }}
                    >
                      Cancel
                    </button>
                    <button
                      className="block w-full text-left px-3 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded-b-lg"
                      onClick={() => { setMenuOpen(false); onModify && onModify(service); }}
                    >
                      Modify
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right: price area */}
          <div className="flex items-center gap-2 flex-col">
            <div className="flex items-center gap-2 relative" ref={breakdownRef}>
              {/* 🔒 Show hint until service + room type are both selected */}
              {!isServiceFullyConfigured ? (
                <span className="text-xs text-gray-400 italic">
                  Select service &amp; room type to see price
                </span>
              ) : priceLoading ? (
                <span className="text-sm text-gray-500">Calculating...</span>
              ) : (
                <>
                  {service.price > 0 && (
                    <button
                      onClick={() => setShowBreakdown((prev) => !prev)}
                      className="text-gray-400 hover:text-gray-600 transition"
                      title="View price breakdown"
                    >
                      <FaInfoCircle size={16} />
                    </button>
                  )}

                  {originalSnapshot.current?.price != null &&
                  service.price !== originalSnapshot.current.price ? (
                    <>
                      <span className="text-gray-400 line-through text-sm">
                        ₹{originalSnapshot.current.price.toLocaleString()}
                      </span>
                      <span className="font-bold text-green-600">
                        ₹{service.price?.toLocaleString() ?? "0"}
                      </span>
                    </>
                  ) : (
                    <span className="font-bold text-green-600">
                      ₹{service.price?.toLocaleString() ?? "0"}
                    </span>
                  )}

                  {hasChanges && (
                    <button
                      onClick={handleReset}
                      className="bg-white border border-gray-300 rounded-full p-1.5 hover:bg-gray-50 transition"
                      title="Reset to original values"
                    >
                      <FaUndo size={14} className="text-gray-500" />
                    </button>
                  )}

                  {showBreakdown && service.price > 0 && (
                    <div className="absolute top-full right-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-200 z-50 p-4 text-sm">
                      <div className="font-semibold text-gray-800 mb-3 border-b pb-2">
                        Price Breakdown
                      </div>
                      <div className="space-y-1.5">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Base Price</span>
                          <span>
                            ₹
                            {(
                              (service.base_total ||
                              service.price - (service.gst_amount || 0)) - (service.surchargesTotal || 0)
                            ).toLocaleString()}
                          </span>
                        </div>

                        {(service.surchargeDetails || []).length > 0 && (
                          <div className="pl-3 border-l-2 border-gray-200 ml-1">
                            {service.surchargeDetails.map((s, i) => (
                              <div key={i} className="flex justify-between">
                                <span className="text-gray-500 text-xs capitalize">
                                  {s.name?.replace(/_/g, " ") || "Surcharge"}
                                </span>
                                <span className="text-xs text-green-600">
                                  ₹{s.price?.toLocaleString() || 0}
                                </span>
                              </div>
                            ))}
                            <div className="flex justify-between font-medium text-xs border-t border-gray-100 pt-1 mt-1">
                              <span>Total surcharges</span>
                              <span className="text-green-600">
                                +₹{(service.surchargesTotal || 0).toLocaleString()}
                              </span>
                            </div>
                          </div>
                        )}

                        {service.discount_amount > 0 && (
                          <div className="flex justify-between text-red-500">
                            <span>Discount</span>
                            <span>-₹{service.discount_amount?.toLocaleString() || 0}</span>
                          </div>
                        )}

                        {service.gst_amount > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">
                              Tax ({service.tax_percentage || 0}%)
                            </span>
                            <span>₹{service.gst_amount?.toLocaleString() || 0}</span>
                          </div>
                        )}

                        <div className="flex justify-between font-bold text-green-700 border-t pt-1.5 mt-1.5">
                          <span>Total</span>
                          <span>₹{service.price?.toLocaleString() || 0}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {originalSnapshot.current?.price != null &&
                    service.price !== originalSnapshot.current.price && (
                      <div className="absolute top-full right-0 mt-1 text-xs font-medium">
                        {service.price > originalSnapshot.current.price ? (
                          <span className="text-red-500">
                            +₹{(service.price - originalSnapshot.current.price).toLocaleString()}
                          </span>
                        ) : (
                          <span className="text-green-600">
                            -₹{(originalSnapshot.current.price - service.price).toLocaleString()}
                          </span>
                        )}
                      </div>
                    )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Service + Room Type + Room Number row */}
        <div className="flex items-center gap-2 mb-2">
          {readOnly ? (
            <>
              <Typography variant="body2" sx={{ fontWeight: 500, color: "#1f2937", minWidth: "12rem" }}>
                {selectedService?.name || service.serviceName || "—"}
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b7280" }}>
                {selectedRoomType?.room_name || service.roomType || "—"}
              </Typography>
              <span className="text-gray-400">–</span>
              <Typography variant="body2" sx={{ color: "#6b7280" }}>
                {selectedRoom?.room_number || service.roomNumber || "—"}
              </Typography>
            </>
          ) : (
            <>
              <TextField
                select
                size="small"
                value={serviceId}
                onChange={(e) => {
                  hasUserInteracted.current = true;
                  const newServiceId = e.target.value;
                  setServiceId(newServiceId);
                  setRoomTypeId("");
                  setRoomId("");
                  // Reset room type + price so the parent's validation kicks in
                  onUpdate({
                    ...service,
                    serviceId: newServiceId,
                    service_id: newServiceId,
                    serviceName: boardingServices.find((s) => s._id === newServiceId)?.name || "",
                    roomTypeId: "",
                    room_type_id: "",
                    roomId: "",
                    room_id: "",
                    roomType: "",
                    roomNumber: "",
                    price: 0,
                    unit_price: 0,
                    sale_price: 0,
                    tax_percentage: 0,
                    gst_amount: 0,
                    discount_amount: 0,
                    surchargeDetails: [],
                    surchargesTotal: 0,
                  });
                }}
                SelectProps={{
                  MenuProps: { style: { zIndex: 99999 }, disablePortal: false },
                }}
                placeholder="Select service"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PetsIcon sx={{ color: "#6b7280", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: "32rem",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "white",
                    border: "1px solid #e5e7eb",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      borderColor: "#10b981",
                      boxShadow: "0 4px 12px rgba(16, 185, 129, 0.15)",
                    },
                    "&.Mui-focused": {
                      borderColor: "#10b981",
                      boxShadow: "0 0 0 3px rgba(16, 185, 129, 0.1)",
                    },
                  },
                }}
              >
                {boardingServices && boardingServices.length > 0 ? (
                  boardingServices.map((srv) => {
                    const mrpPrice = Number(srv.mrp_price) || Number(srv.basic_price || 0);
                    const salePrice = Number(srv.sale_price) || mrpPrice;
                    const isFree = srv.discount_percentage === 100;
                    const hasDiscount = salePrice < mrpPrice || isFree;
                    return (
                      <MenuItem key={srv._id} value={srv._id}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span>{srv?.name}</span>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {hasDiscount ? (
                              <>
                                <Typography component="span" sx={{ textDecoration: "line-through", color: "#9ca3af", fontSize: "12px" }}>
                                  ₹{mrpPrice}
                                </Typography>
                                <Typography component="span" sx={{ color: "#10b981", fontWeight: "bold", fontSize: "14px" }}>
                                  {isFree ? "FREE" : `₹${salePrice}`}
                                </Typography>
                                {srv.discount_percentage > 0 && (
                                  <Chip
                                    label={`${srv.discount_percentage}% OFF`}
                                    size="small"
                                    sx={{
                                      backgroundColor: isFree ? "#dcfce7" : "#fef3c7",
                                      color: isFree ? "#166534" : "#d97706",
                                      fontSize: "10px",
                                      height: "20px",
                                      "& .MuiChip-label": { px: 1 },
                                    }}
                                  />
                                )}
                              </>
                            ) : (
                              <Typography component="span" sx={{ color: "#10b981", fontWeight: "bold", fontSize: "14px" }}>
                                ₹{mrpPrice}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </MenuItem>
                    );
                  })
                ) : (
                  <MenuItem value="" disabled>No services available</MenuItem>
                )}
              </TextField>

              <CustomSelect
                value={roomTypeId}
                onChange={(value) => {
                  hasUserInteracted.current = true;
                  setRoomTypeId(value);
                  setRoomId("");
                  onUpdate({
                    ...service,
                    roomTypeId: value,
                    room_type_id: value,
                    roomId: "",
                    room_id: "",
                    roomType: roomTypes.find((rt) => rt._id === value)?.room_name || "",
                    roomNumber: "",
                  });
                }}
                disabled={!serviceId || loadingRoomTypes}
                loading={loadingRoomTypes}
                options={roomTypes}
                placeholder="Room type"
              />

              <span className="text-gray-400">–</span>

              <CustomSelect
                value={roomId}
                onChange={(value) => {
                  setRoomId(value);
                  onUpdate({
                    ...service,
                    roomId: value,
                    room_id: value,
                    roomNumber:
                      availableRooms.find((r) => r._id === value)?.room_number || "",
                  });
                }}
                disabled={!roomTypeId || loadingRooms}
                loading={loadingRooms}
                options={availableRooms}
                placeholder="Room #"
              />
            </>
          )}
        </div>

        {/* Addons / products / other charges */}
        <BookingAddonsSection
          addons={service.addons || []}
          products={service.products || []}
          otherCharges={service.otherCharges || []}
          onAddonsChange={(newAddons) => onUpdate({ ...service, addons: newAddons })}
          onProductsChange={(newProducts) => onUpdate({ ...service, products: newProducts })}
          onOtherChargesChange={(newCharges) => onUpdate({ ...service, otherCharges: newCharges })}
          serviceCategoryId={selectedService?.category_type_id}
          petTypeId={pet.pet_type_id}
          serviceTypeName={"Boarding"}
        />

        {/* Daily care tabs */}
        <div className="mt-3 pt-3 border-t border-gray-200">
          {sourceForm && (
            <div className="mb-2 flex items-center justify-between rounded-lg bg-indigo-50 border border-indigo-100 px-3 py-2">
              <span className="text-xs text-indigo-700">
                Pre-filled from a submitted customer form
                {sourceForm.submitted_at && (
                  <>
                    {" · "}
                    {new Date(sourceForm.submitted_at).toLocaleDateString(
                      "en-IN",
                    )}
                  </>
                )}
              </span>
              <button
                type="button"
                onClick={() => {
                  onUpdate({
                    ...service,
                    feeding: [],
                    walking: [],
                    medication: [],
                    belongings: [],
                  });
                  clearPrefill();
                  prefillAppliedRef.current = false;
                }}
                className="text-xs font-semibold text-indigo-700 underline hover:text-indigo-900"
              >
                Clear
              </button>
            </div>
          )}
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-semibold text-gray-800 whitespace-nowrap">
              Daily Care Task
            </h3>
            <div className="flex gap-1 overflow-x-auto">
              {["Feeding", "Walking", "Medication", "Belongings"].map((label, idx) => (
                <button
                  key={idx}
                  className={`text-xs font-medium px-3 py-1 rounded-full transition whitespace-nowrap ${
                    activeTab === idx
                      ? "bg-indigo-100 text-indigo-700 shadow-sm"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                  onClick={() => setActiveTab(idx)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-2 bg-gray-50 rounded-lg p-3 max-h-60 overflow-y-auto">
            {/* Feeding */}
            {activeTab === 0 && (
              <div className="space-y-2">
                {(service.feeding || []).map((item, idx) => {
                  const times = item.periods || (item.period ? [item.period] : []);
                  return (
                    <div key={idx} className="bg-white border border-gray-200 rounded-xl p-3 flex items-start gap-2 text-sm shadow-sm">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <DateChips dates={item.dates} />
                          {item.dates?.length > 0 && times.length > 0 && <span className="text-gray-300">|</span>}
                          {times.map((time) => (
                            <Chip key={time} label={time} size="small" color="primary" variant="outlined" />
                          ))}
                        </div>
                        <div className="text-gray-700">{item.whatToFeed}</div>
                      </div>
                      <button onClick={() => removeFeeding(idx)} className="text-red-400 hover:text-red-600 flex-shrink-0 mt-1">
                        <FaTimesCircle size={16} />
                      </button>
                    </div>
                  );
                })}
                <div className="space-y-2">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm flex-wrap">
                      <span className="font-medium text-gray-700">When:</span>
                      {boardingDates.map((dateObj) => {
                        const dateStr = formatLocalDate(dateObj);
                        return (
                          <FormControlLabel
                            key={dateStr}
                            control={
                              <Checkbox
                                size="small"
                                checked={feedDates.includes(dateStr)}
                                onChange={() => {
                                  setFeedDates((prev) =>
                                    prev.includes(dateStr)
                                      ? prev.filter((date) => date !== dateStr)
                                      : [...prev, dateStr]
                                  );
                                }}
                              />
                            }
                            label={formatDateDisplay(dateObj)}
                            sx={{ mr: 1, "& .MuiFormControlLabel-label": { fontSize: "0.875rem" } }}
                          />
                        );
                      })}
                    </div>
                    <div className="flex items-center gap-2 text-sm flex-wrap">
                      <span className="font-medium text-gray-700 invisible">When:</span>
                      {["Morning", "Noon", "Evening"].map((period) => (
                        <FormControlLabel
                          key={period}
                          control={
                            <Checkbox
                              size="small"
                              checked={feedPeriods.includes(period)}
                              onChange={() => {
                                setFeedPeriods((prev) =>
                                  prev.includes(period)
                                    ? prev.filter((p) => p !== period)
                                    : [...prev, period]
                                );
                              }}
                            />
                          }
                          label={period}
                          sx={{ mr: 1, "& .MuiFormControlLabel-label": { fontSize: "0.875rem" } }}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-start">
                    <TextField
                      size="small"
                      label="What to feed"
                      placeholder="e.g., 1 cup kibble"
                      value={feedWhat}
                      onChange={(e) => setFeedWhat(e.target.value)}
                      sx={{ gridColumn: "span 8" }}
                    />
                    <button
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-1.5 justify-center sm:col-span-4"
                      onClick={() => {
                        if (feedPeriods.length && feedWhat && feedDates.length) {
                          addFeeding({ periods: feedPeriods, whatToFeed: feedWhat }, feedDates);
                          setFeedPeriods([]);
                          setFeedDates([]);
                          setFeedWhat("");
                        }
                      }}
                      disabled={!feedPeriods.length || !feedWhat || !feedDates.length}
                    >
                      <FaPlus size={12} /> Add Feeding
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Walking */}
            {activeTab === 1 && (
              <div className="space-y-2">
                {(service.walking || []).map((item, idx) => {
                  const times = item.times || (item.time ? [item.time] : []);
                  return (
                    <div key={idx} className="bg-white border border-gray-200 rounded-xl p-3 flex items-start gap-2 text-sm shadow-sm">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <DateChips dates={item.dates} />
                          {item.dates?.length > 0 && times.length > 0 && <span className="text-gray-300">|</span>}
                          {times.map((time) => (
                            <Chip key={time} label={time} size="small" color="primary" variant="outlined" />
                          ))}
                        </div>
                        <div className="text-gray-700">
                          {item.duration ? `${item.duration} min` : ""}
                          {item.duration && item.notes ? " • " : ""}
                          {item.notes || ""}
                        </div>
                      </div>
                      <button onClick={() => removeWalking(idx)} className="text-red-400 hover:text-red-600 flex-shrink-0 mt-1">
                        <FaTimesCircle size={16} />
                      </button>
                    </div>
                  );
                })}
                <div className="space-y-2">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm flex-wrap">
                      <span className="font-medium text-gray-700">When:</span>
                      {boardingDates.map((dateObj) => {
                        const dateStr = formatLocalDate(dateObj);
                        return (
                          <FormControlLabel
                            key={dateStr}
                            control={
                              <Checkbox
                                size="small"
                                checked={walkDates.includes(dateStr)}
                                onChange={() => {
                                  setWalkDates((prev) =>
                                    prev.includes(dateStr)
                                      ? prev.filter((date) => date !== dateStr)
                                      : [...prev, dateStr]
                                  );
                                }}
                              />
                            }
                            label={formatDateDisplay(dateObj)}
                            sx={{ mr: 1, "& .MuiFormControlLabel-label": { fontSize: "0.875rem" } }}
                          />
                        );
                      })}
                    </div>
                    <div className="flex items-center gap-2 text-sm flex-wrap">
                      <span className="font-medium text-gray-700 invisible">When:</span>
                      {["Morning", "Noon", "Evening"].map((time) => (
                        <FormControlLabel
                          key={time}
                          control={
                            <Checkbox
                              size="small"
                              checked={walkTimes.includes(time)}
                              onChange={() => {
                                setWalkTimes((prev) =>
                                  prev.includes(time)
                                    ? prev.filter((t) => t !== time)
                                    : [...prev, time]
                                );
                              }}
                            />
                          }
                          label={time}
                          sx={{ mr: 1, "& .MuiFormControlLabel-label": { fontSize: "0.875rem" } }}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
                    <TextField
                      size="small"
                      label="Duration (min)"
                      type="number"
                      placeholder="30"
                      value={walkDur}
                      onChange={(e) => setWalkDur(e.target.value)}
                      sx={{ gridColumn: "span 4" }}
                    />
                    <TextField
                      size="small"
                      label="Notes"
                      placeholder="e.g., leash, park"
                      value={walkNotes}
                      onChange={(e) => setWalkNotes(e.target.value)}
                      sx={{ gridColumn: "span 4" }}
                    />
                    <button
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-1.5 justify-center sm:col-span-4"
                      onClick={() => {
                        if (walkTimes.length && walkDur && walkDates.length) {
                          addWalking({ times: walkTimes, duration: walkDur, notes: walkNotes || "" }, walkDates);
                          setWalkTimes([]);
                          setWalkDates([]);
                          setWalkDur("");
                          setWalkNotes("");
                        }
                      }}
                      disabled={!walkTimes.length || !walkDur || !walkDates.length}
                    >
                      <FaPlus size={12} /> Add Walking
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Medication */}
            {activeTab === 2 && (
              <div className="space-y-2">
                {(service.medication || []).map((item, idx) => (
                  <div key={idx} className="bg-white border border-gray-200 rounded-xl p-3 flex items-start gap-2 text-sm shadow-sm">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <DateChips dates={item.dates} />
                        {item.dates?.length > 0 && item.periods?.length > 0 && <span className="text-gray-300">|</span>}
                        {(item.periods || []).map((p) => (
                          <Chip key={p} label={p} size="small" color="primary" variant="outlined" />
                        ))}
                      </div>
                      <div className="text-gray-700">
                        <span className="font-medium">{item.name}</span>
                        {item.quantity ? ` • ${item.quantity}` : ""}
                        {item.route ? ` • ${item.route}` : ""}
                        {item.instructions ? ` • ${item.instructions}` : ""}
                      </div>
                    </div>
                    <button onClick={() => removeMedication(idx)} className="text-red-400 hover:text-red-600 flex-shrink-0 mt-1">
                      <FaTimesCircle size={16} />
                    </button>
                  </div>
                ))}
                <div className="space-y-2">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm flex-wrap">
                      <span className="font-medium text-gray-700">When:</span>
                      {boardingDates.map((dateObj) => {
                        const dateStr = formatLocalDate(dateObj);
                        return (
                          <FormControlLabel
                            key={dateStr}
                            control={
                              <Checkbox
                                size="small"
                                checked={medDates.includes(dateStr)}
                                onChange={() => {
                                  setMedDates((prev) =>
                                    prev.includes(dateStr)
                                      ? prev.filter((date) => date !== dateStr)
                                      : [...prev, dateStr]
                                  );
                                }}
                              />
                            }
                            label={formatDateDisplay(dateObj)}
                            sx={{ mr: 1, "& .MuiFormControlLabel-label": { fontSize: "0.875rem" } }}
                          />
                        );
                      })}
                    </div>
                    <div className="flex items-center gap-2 text-sm flex-wrap">
                      <span className="font-medium text-gray-700 invisible">When:</span>
                      {["Morning", "Noon", "Evening"].map((period) => (
                        <FormControlLabel
                          key={period}
                          control={
                            <Checkbox
                              size="small"
                              checked={medPeriods.includes(period)}
                              onChange={() => {
                                setMedPeriods((prev) =>
                                  prev.includes(period)
                                    ? prev.filter((p) => p !== period)
                                    : [...prev, period]
                                );
                              }}
                            />
                          }
                          label={period}
                          sx={{ mr: 1, "& .MuiFormControlLabel-label": { fontSize: "0.875rem" } }}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
                    <TextField size="small" label="Medicine name" value={medName} onChange={(e) => setMedName(e.target.value)} sx={{ gridColumn: "span 3" }} />
                    <TextField size="small" label="Qty" placeholder="1 tablet" value={medQty} onChange={(e) => setMedQty(e.target.value)} sx={{ gridColumn: "span 1" }} />
                    <TextField select size="small" label="Route" value={medRoute} onChange={(e) => setMedRoute(e.target.value)} sx={{ gridColumn: "span 2" }}>
                      <MenuItem value="oral">Oral</MenuItem>
                      <MenuItem value="topical">Topical</MenuItem>
                      <MenuItem value="injection">Injection</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </TextField>
                    <TextField size="small" label="Instructions" placeholder="with food, etc." value={medInstr} onChange={(e) => setMedInstr(e.target.value)} sx={{ gridColumn: "span 3" }} />
                    <button
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-1.5 justify-center sm:col-span-3"
                      onClick={() => {
                        if (medPeriods.length && medName && medRoute && medDates.length) {
                          addMedication(
                            { periods: medPeriods, name: medName, route: medRoute, quantity: medQty, instructions: medInstr },
                            medDates
                          );
                          setMedPeriods([]);
                          setMedDates([]);
                          setMedName("");
                          setMedQty("");
                          setMedRoute("");
                          setMedInstr("");
                        }
                      }}
                      disabled={!medPeriods.length || !medName || !medRoute || !medDates.length}
                    >
                      <FaPlus size={12} /> Add Medication
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Belongings */}
            {activeTab === 3 && (
              <div className="space-y-2">
                {(service.belongings || []).map((item, idx) => (
                  <div key={idx} className="bg-white border border-gray-200 rounded-xl p-3 flex items-center gap-2 text-sm shadow-sm">
                    <div className="flex-1">
                      <span className="font-medium text-gray-800">{item.name}</span>{" "}
                      {item.quantity && <Chip label={`×${item.quantity}`} size="small" variant="outlined" sx={{ ml: 1 }} />}
                      {item.description && <span className="text-gray-400 text-xs ml-2">{item.description}</span>}
                    </div>
                    <button onClick={() => removeBelonging(idx)} className="text-red-400 hover:text-red-600">
                      <FaTimesCircle size={16} />
                    </button>
                  </div>
                ))}
                <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
                  <TextField size="small" label="Item name" value={belongName} onChange={(e) => setBelongName(e.target.value)} sx={{ gridColumn: "span 4" }} />
                  <TextField size="small" label="Qty" type="number" placeholder="1" value={belongQty} onChange={(e) => setBelongQty(e.target.value)} sx={{ gridColumn: "span 1" }} />
                  <TextField size="small" label="Description" placeholder="e.g., red leash" value={belongDesc} onChange={(e) => setBelongDesc(e.target.value)} sx={{ gridColumn: "span 4" }} />
                  <button
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-1.5 justify-center sm:col-span-3"
                    onClick={() => {
                      if (belongName) {
                        addBelonging({ name: belongName, quantity: belongQty || "1", description: belongDesc || "" });
                        setBelongName("");
                        setBelongQty("1");
                        setBelongDesc("");
                      }
                    }}
                    disabled={!belongName}
                  >
                    <FaPlus size={12} /> Add Belonging
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Customer Notes (read-only) */}
        {service.notes && (
          <div className="mt-3 pt-3 border-t border-gray-200 text-sm text-gray-700">
            <span className="font-semibold text-gray-500">Customer Notes:</span>
            <p className="mt-1 whitespace-pre-wrap">{service.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BoardingServiceCheckinCard;
